// frontend/src/components/cohesion/EntityDrawer.Person.tsx
// Person panel for the universal entity drawer.

import { useEffect, useState } from 'react'
import { useLanguage } from '../../i18n/index.jsx'
import api from '../../utils/api.js'

type Lang = { t: (k: string) => string }

interface Props {
  id: number
  navigate: (to: string) => void
  closeDrawer: () => void
}

interface Person {
  id: number
  username?: string
  full_name?: string
  role?: string
  email?: string
}

export default function EntityDrawerPerson({ id, navigate, closeDrawer }: Props) {
  const { t } = useLanguage() as Lang
  const [data, setData] = useState<Person | null>(null)
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    api.get<Person>(`/users/${id}`)
      .then((r) => { if (!cancelled) setData(r.data) })
      .catch((e) => { if (!cancelled) setError(String(e?.response?.data?.error ?? e?.message ?? 'load failed')) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id])

  if (loading) return <div className="entity-drawer-loading">{t('loading')}</div>
  if (error)   return <div className="entity-drawer-error" role="alert">{error}</div>
  if (!data)   return <div className="entity-drawer-empty">{t('itemNotFound')}</div>

  const label = data.full_name || data.username || `User #${data.id}`
  const openPeopleHub = () => {
    closeDrawer()
    navigate(`/people?tab=directory&user=${data.id}`)
  }

  return (
    <>
      <button type="button" className="entity-drawer-title-link" onClick={openPeopleHub}>
        {label}
      </button>
      <div className="entity-drawer-meta">
        {data.role && (
          <div className="entity-drawer-meta-row">
            <span className="entity-drawer-meta-key">{t('role')}</span>
            <span className="entity-drawer-meta-val">{data.role}</span>
          </div>
        )}
        {data.email && (
          <div className="entity-drawer-meta-row">
            <span className="entity-drawer-meta-key">{t('email')}</span>
            <span className="entity-drawer-meta-val">{data.email}</span>
          </div>
        )}
      </div>
      <div className="entity-drawer-actions">
        <button type="button" className="entity-drawer-action" onClick={openPeopleHub}>
          {t('navPeopleDirectory')}
        </button>
        <button type="button" className="entity-drawer-action" onClick={() => { closeDrawer(); navigate(`/people?tab=availability&user=${data.id}`) }}>
          {t('navAvailability')}
        </button>
      </div>
    </>
  )
}
